import type { Ref } from 'vue';
import { watchEffect } from 'vue';
import type { FieldHtmlNameType, HekateDisplayForm } from '@onshape/www-inertia';
import { FieldHtmlNameUtil, Trans } from '@onshape/www-inertia';
import ExternalUtil from '../../../../ExternalUtil';
import BaseProgressiveProfileDefinition from '../BaseProgressiveProfileDefinition';
import type { OnshapeEvaluatingStatus, ProgressRule } from '../ProgressRule';

/** Defines that fields to show, the name of the branch to consider and hide/show rules */
export default class GatedContentProgressiveProfileDefinition extends BaseProgressiveProfileDefinition {
  /**
   * Fields who's actual value is persisted (must not contain PII) so that
   * branches can be considered. Branches are like markets (e.g. "current user,
   * professional, student)
   */
  private readonly branchField = FieldHtmlNameUtil.chooseOnshapeEvaluatingStatus;

  /** Fields that must be shown regardless of rules */
  private readonly alwaysShownFields: readonly FieldHtmlNameType[] = [
    FieldHtmlNameUtil.firstName,
    FieldHtmlNameUtil.lastName,
    FieldHtmlNameUtil.email,
    FieldHtmlNameUtil.companyName,
    FieldHtmlNameUtil.privacyConsentAccepted,
    FieldHtmlNameUtil.submit,
  ];

  /**
   * This list will be searched in order for the first rule to match
   * It can match by any of the `fieldToShow` values being non-empty AND all the `branchTests` passing
   */
  private readonly progressRules: readonly ProgressRule[] = [
    {
      fieldToShow: [FieldHtmlNameUtil.chooseOnshapeEvaluatingStatus],
      branch: '',
    },
    {
      branch: 'professionalOnshapeEvaluator',
      fieldToShow: [
        FieldHtmlNameUtil.jobTitle,
        FieldHtmlNameUtil.phoneNumber,
        FieldHtmlNameUtil.primaryCadSystem,
        FieldHtmlNameUtil.numberOfCadUsers1,
      ],
    },
    {
      branch: 'nonProfessionalOnshapeEvaluator',
      fieldToShow: [FieldHtmlNameUtil.primaryCadSystem, FieldHtmlNameUtil.phoneNumber],
    },
    {
      branch: 'currentOnshapeUser',
      fieldToShow: [FieldHtmlNameUtil.jobTitle, FieldHtmlNameUtil.phoneNumber],
    },
  ];

  public getProgressRules(): readonly ProgressRule[] {
    return this.progressRules;
  }

  public getBranchField(): FieldHtmlNameType {
    return this.branchField;
  }

  public getAlwaysShownFields(): readonly FieldHtmlNameType[] {
    return this.alwaysShownFields;
  }

  /**
   * Watch for changes to branch to show the "companyName" field with the correct value
   */
  override realtimeBranchModifications({
    formDefinition,
  }: {
    formDefinition: Ref<HekateDisplayForm>;
  }): void {
    super.realtimeBranchModifications({ formDefinition });

    const evaluationStatusField = formDefinition.value.displayFields.find(
      (field) => field.htmlName === this.branchField
    );
    if (!evaluationStatusField) {
      return;
    }

    const companyNameField = ExternalUtil.findDisplayFieldOrThrow({
      formDefinition,
      fieldName: FieldHtmlNameUtil.companyName,
    });

    companyNameField.componentName = 'hidden-field';

    // Show the company name field with the correct value
    watchEffect(() => {
      const chosenBranch = evaluationStatusField.val as OnshapeEvaluatingStatus;

      companyNameField.isRequired = true;
      companyNameField.componentName = 'text-input-field';

      switch (chosenBranch) {
        case '': // base case, reset and return
          return;
        case 'studentOrEducatorOnshapeUser':
          companyNameField.name = Trans.byKey('schoolName');
          break;
        default:
          companyNameField.name = Trans.byKey('companyName');
      }
    });
  }

  public override onSuccess({
    formDefinition,
    redirectUrl,
  }: {
    formDefinition: Ref<HekateDisplayForm>;
    redirectUrl: string | null;
  }): void {
    super.onSuccess({ formDefinition, redirectUrl });
  }
}
